import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  Alert,
  Snackbar
} from '@mui/material';
import { RankingItem } from '../types/project-form';

export interface RankingCriteria {
  key: keyof RankingItem;
  label: string;
  description: string;
  options: Array<{
    value: number;
    label: string;
  }>;
}

interface RankingComponentProps {
  title: string;
  items: RankingItem[];
  criteria: RankingCriteria[];
  onItemsChange: (items: RankingItem[]) => void;
  emptyMessage?: string;
  showGuide?: boolean;
  itemColumnTitle?: string;
}

const RankingComponent: React.FC<RankingComponentProps> = ({
  title,
  items,
  criteria,
  onItemsChange,
  emptyMessage = "Add items above to begin ranking.",
  showGuide = true,
  itemColumnTitle = "Item"
}) => {
  // Tracks a cell whose value was auto-cleared so it can be highlighted
  const [clearedCell, setClearedCell] = useState<{ itemId: string; criteriaKey: keyof RankingItem } | null>(null);
  const [notice, setNotice] = useState<string>('');
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
  }, []);

  const updateItemScore = (itemId: string, criteriaKey: keyof RankingItem, value: number) => {
    // A value may only be assigned once per criterion - find any other item using it
    const conflictingItem = items.find(
      other => other.id !== itemId && other[criteriaKey] === value
    );

    const updatedItems = items
      .map(item => {
        if (item.id === itemId) {
          return { ...item, [criteriaKey]: value };
        }
        // Clear the duplicate from the other item, leaving its field blank
        if (conflictingItem && item.id === conflictingItem.id) {
          return { ...item, [criteriaKey]: undefined };
        }
        return item;
      })
      .map(item => ({
        ...item,
        total: criteria.reduce((sum, criterion) => {
          return sum + (item[criterion.key] as number || 0);
        }, 0)
      }));

    // Recalculate ranks based on total scores
    const sortedItems = [...updatedItems].sort((a, b) => (b.total || 0) - (a.total || 0));
    const rankedItems = updatedItems.map(item => {
      const sortedIndex = sortedItems.findIndex(sortedItem => sortedItem.id === item.id);
      return { ...item, rank: sortedIndex + 1 };
    });

    onItemsChange(rankedItems);

    // Notify the user and highlight the cell that was cleared
    if (conflictingItem) {
      const criterion = criteria.find(c => c.key === criteriaKey);
      const option = criterion?.options.find(o => o.value === value);
      const currentItem = items.find(i => i.id === itemId);

      setClearedCell({ itemId: conflictingItem.id, criteriaKey });
      setNotice(
        `"${option?.label ?? value}" under ${criterion?.label ?? String(criteriaKey)} was cleared for "${conflictingItem.text || 'another item'}" because the same choice was selected for "${currentItem?.text || 'this item'}".`
      );

      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
      clearTimerRef.current = setTimeout(() => setClearedCell(null), 6000);
    }
  };

  if (items.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2, mt: 2 }}>
        {emptyMessage}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
        {title}
      </Typography>
      

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.50' }}>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 200 }}>{itemColumnTitle}</TableCell>
              {criteria.map((criterion) => (
                <TableCell 
                  key={criterion.key} 
                  align="center" 
                  sx={{ fontWeight: 'bold', minWidth: 120 }}
                >
                  <div>{criterion.label}</div>
                  <Typography variant="caption" sx={{ fontWeight: 'normal', color: 'text.secondary', fontStyle: 'italic' }}>
                    {criterion.description}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>Total</TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 80 }}>Rank</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id}>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {item.text || `Item ${index + 1}`}
                </TableCell>
                {criteria.map((criterion) => (
                  <TableCell
                    key={criterion.key}
                    align="center"
                    sx={{
                      transition: 'background-color 0.4s',
                      ...(clearedCell &&
                        clearedCell.itemId === item.id &&
                        clearedCell.criteriaKey === criterion.key
                          ? { bgcolor: 'warning.light' }
                          : {})
                    }}
                  >
                    <Select
                      size="small"
                      value={item[criterion.key] ?? ''}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        updateItemScore(item.id, criterion.key, value);
                      }}
                      displayEmpty
                      sx={{
                        minWidth: 100,
                        width: '100%',
                        '& .MuiOutlinedInput-root': {
                          '&:hover fieldset': {
                            borderColor: 'primary.main',
                          },
                        }
                      }}
                    >
                      {criterion.options.map((option) => (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                        >
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                ))}
                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 40,
                      height: 32,
                      borderRadius: 1,
                      bgcolor: item.total ? 'primary.100' : 'grey.100',
                      color: item.total ? 'primary.800' : 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}
                  >
                    {item.total || 0}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 32,
                      height: 32,
                      borderRadius: '50%',
                      bgcolor: item.rank === 1 ? 'success.main' : 
                              item.rank === 2 ? 'warning.main' : 
                              item.rank === 3 ? 'info.main' : 'grey.300',
                      color: item.rank && item.rank <= 3 ? 'white' : 'text.secondary',
                      fontWeight: 'bold',
                      fontSize: '0.875rem'
                    }}
                  >
                    {item.rank || '-'}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={!!notice}
        autoHideDuration={6000}
        onClose={() => setNotice('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="warning" variant="filled" onClose={() => setNotice('')}>
          {notice}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RankingComponent;
